import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useLayoutEffect, useState } from "react";
import { isApiError, programApi, type ClassLearningPageParams } from "../api";
import type { Schema } from "../types/generated";
import { useAuth } from "./useAuth";

export interface ClassLearningQueryOptions extends ClassLearningPageParams {
  enabled?: boolean;
}

export type ClassLearningQuery<T> = Omit<UseQueryResult<T, unknown>, "data"> & {
  data: T | undefined;
};
export type ClassFeedbackDraft = Omit<Schema["ClassSessionFeedbackWrite"], "enrollment_id">;
export interface ClassFeedbackSaveContext {
  queryKey: readonly unknown[];
}

export const classLearningQueryKeys = {
  actor: (actor: string | null) => ["class-learning", actor] as const,
  learners: (actor: string | null, programId: string) =>
    ["class-learning", actor, "learners", programId] as const,
  progress: (actor: string | null, programId: string, enrollmentId: string) =>
    ["class-learning", actor, "progress", programId, enrollmentId] as const,
  attendance: (actor: string | null, trackId: string, occurrenceStart: string) =>
    ["class-learning", actor, "attendance", trackId, occurrenceStart] as const,
};

function pageParams(options: ClassLearningQueryOptions): ClassLearningPageParams {
  const page: ClassLearningPageParams = {};
  if (options.limit !== undefined) page.limit = options.limit;
  if (options.offset !== undefined) page.offset = options.offset;
  return page;
}

function useClassQuery<T>(
  scope: readonly unknown[], read: (signal: AbortSignal) => Promise<T>,
  options: ClassLearningQueryOptions, hasContext: boolean,
): ClassLearningQuery<T> {
  const { user, loading } = useAuth();
  const enabled = Boolean(user?.uid) && !loading && hasContext && options.enabled !== false;
  const queryKey = [...classLearningQueryKeys.actor(user?.uid ?? null), ...scope, options.limit ?? null, options.offset ?? null];
  const signature = JSON.stringify(queryKey);
  const [fetchedScope, setFetchedScope] = useState<string | null>(null);
  const query = useQuery<T, unknown>({
    queryKey,
    queryFn: ({ signal }) => read(signal),
    enabled,
    retry: false,
    refetchOnMount: "always",
  });
  useLayoutEffect(() => { setFetchedScope(null); }, [signature, enabled]);
  useLayoutEffect(() => {
    if (enabled && query.isFetchedAfterMount) setFetchedScope(signature);
  }, [signature, enabled, query.isFetchedAfterMount]);
  // resetQueries zeroes TanStack's update counters. Remember a completed read
  // for this mounted scope so the subsequent fresh response can be displayed.
  // Reset still clears the body, and actor/source changes clear this latch.
  const hasFetched = query.isFetchedAfterMount || fetchedScope === signature;
  return {
    ...query,
    isLoading: enabled && !query.isError && (!hasFetched || query.isLoading),
    data: enabled && hasFetched && !query.isError ? query.data : undefined,
  };
}

export function useClassLearningLearners(
  programId: string | undefined, options: ClassLearningQueryOptions = {},
): ClassLearningQuery<Schema["ClassLearningLearners"]> {
  return useClassQuery(
    ["learners", programId ?? ""],
    (signal) => programApi.listLearningLearners(programId as string, pageParams(options), signal),
    options, Boolean(programId),
  );
}

export function useClassLearningProgress(
  programId: string | undefined, enrollmentId: string | undefined,
  options: ClassLearningQueryOptions = {},
): ClassLearningQuery<Schema["ClassLearningProgress"]> {
  return useClassQuery(
    ["progress", programId ?? "", enrollmentId ?? ""],
    (signal) => programApi.getLearningProgress(programId as string, enrollmentId as string, pageParams(options), signal),
    options, Boolean(programId && enrollmentId),
  );
}

export function useClassAttendanceRoster(
  trackId: string | undefined, occurrenceStart: string | undefined,
  options: ClassLearningQueryOptions = {},
): ClassLearningQuery<Schema["ClassAttendanceRoster"]> {
  return useClassQuery(
    ["attendance", trackId ?? "", occurrenceStart ?? ""],
    (signal) => programApi.getAttendanceRoster(trackId as string, occurrenceStart as string, pageParams(options), signal),
    options, Boolean(trackId && occurrenceStart),
  );
}

export function useSaveClassSessionFeedback(
  programId: string | undefined, enrollmentId: string | undefined,
): UseMutationResult<Schema["ClassSessionFeedback"], unknown, ClassFeedbackDraft, ClassFeedbackSaveContext> {
  const { user, loading } = useAuth();
  const client = useQueryClient();
  function target() {
    if (!user?.uid || loading || !programId || !enrollmentId) {
      throw new Error("Sign in and select an enrollment before saving feedback.");
    }
    return { actor: user.uid, programId, enrollmentId };
  }
  return useMutation<Schema["ClassSessionFeedback"], unknown, ClassFeedbackDraft, ClassFeedbackSaveContext>({
    mutationKey: [...classLearningQueryKeys.progress(user?.uid ?? null, programId ?? "", enrollmentId ?? ""), "save"],
    retry: false,
    onMutate: () => {
      const scope = target();
      return { queryKey: classLearningQueryKeys.progress(scope.actor, scope.programId, scope.enrollmentId) };
    },
    mutationFn: (draft) => {
      const scope = target();
      return programApi.saveSessionFeedback(scope.programId, {
        enrollment_id: scope.enrollmentId,
        recurrence_start: draft.recurrence_start,
        expected_revision: draft.expected_revision,
        content: draft.content,
      });
    },
    onSuccess: async (_feedback, _draft, scope) => {
      // Refetch authoritative data; never install an old actor's returned body.
      if (scope) await client.invalidateQueries({ queryKey: scope.queryKey });
    },
    onError: async (error, _draft, scope) => {
      if (scope && isApiError(error) && [401, 403, 404, 409].includes(error.status)) {
        // A confirmed authority/revision failure invalidates the displayed body,
        // not just its freshness. Fetch again without replaying the write.
        await client.resetQueries({ queryKey: scope.queryKey });
      }
    },
  });
}
